import { json, type DataFunctionArgs } from '@remix-run/node'
import {
	// Form,
	Link,
	useLoaderData,
	type MetaFunction,
} from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
// import { Icon } from '#app/components/ui/icon.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getPersonaImgSrc, invariantResponse } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

export async function loader({ params, request }: DataFunctionArgs) {
	const userId = await getUserId(request)
	const persona = await prisma.persona.findFirst({
		select: {
			id: true,
			name: true,
			ethnicity: true,
			gender: true,
			bio: true,
			images: true,
			createdAt: true,
			ownerId: true,
			kinks: true,
			sexualities: true,
			sexualRoles: true,
			traits: true,
			age: true,
		},
		where: {
			id: params.personaId,
			OR: [
				{
					private: false,
				},
				{
					private: true,
					ownerId: userId ?? '',
				},
			],
			AND: {
				id: params.personaId,
			},
		},
	})

	invariantResponse(persona, 'Persona not found', { status: 404 })

	// what about shared personas?

	return json({
		persona,
		personaCreatedDisplay: persona.createdAt.toLocaleDateString(),
	})
}

export default function PersonaRoute() {
	const data = useLoaderData<typeof loader>()
	const persona = data.persona
	const loggedInUser = useOptionalUser()
	const isOwner = data.persona.ownerId === loggedInUser?.id

	return (
		<div className="container mb-48 mt-36 flex flex-col items-center justify-center">
			<Spacer size="4xs" />

			<div className="container flex flex-col items-center rounded-3xl bg-muted p-12">
				<div className="relative w-52">
					<div className="absolute -top-40">
						<div className="relative">
							<img
								src={getPersonaImgSrc(
									data.persona.images?.length > 0
										? data.persona.images[0].id
										: '',
								)}
								alt={persona.name}
								className="h-52 w-52 rounded-full object-cover"
							/>
						</div>
					</div>
				</div>

				<Spacer size="sm" />

				<div className="flex flex-col items-center">
					<div className="flex flex-wrap items-center justify-center gap-4">
						<h1 className="text-center text-h2">
							{persona.name} ({persona.age})
						</h1>
					</div>
					<p className="mb-4 mt-2 text-center text-muted-foreground">
						<Link
							to={`/personas?search=${persona.ethnicity}`}
							prefetch="intent"
						>
							{persona.ethnicity}
						</Link>
					</p>
					<div className="mt-4 flex flex-wrap items-center justify-center gap-4">
						{persona.sexualities.map(type => (
							<Badge key={type.name} variant="default">
								<Link to={`/personas?search=${type.name}`} prefetch="intent">
									{type.name}
								</Link>
							</Badge>
						))}
						{persona.traits.map(type => (
							<Badge key={type.name} variant="default">
								<Link to={`/personas?search=${type.name}`} prefetch="intent">
									{type.name}
								</Link>
							</Badge>
						))}
					</div>
					<div className="mt-2 flex flex-wrap items-center justify-center gap-4">
						{persona.kinks.map(kink => (
							<Badge key={kink.name} variant="outline">
								<Link to={`/personas?search=${kink.name}`} prefetch="intent">
									{kink.name}
								</Link>
							</Badge>
						))}
						{persona.sexualRoles.map(type => (
							<Badge key={type.name} variant="outline">
								<Link to={`/personas?search=${type.name}`} prefetch="intent">
									{type.name}
								</Link>
							</Badge>
						))}
					</div>

					<p className="mt-10 text-center">{data.persona.bio}</p>
					<div className="mt-10 flex gap-4">
						{isOwner ? (
							<Button>
								<Link to="edit">Edit Persona</Link>
							</Button>
						) : (
							<Button>
								<Link to={`/chat/new/${persona.id}`} prefetch="intent">
									New Chat
								</Link>
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	return [
		{ title: `${data?.persona.name} | mykink.ai` },
		{
			name: 'description',
			content: `Profile of ${data?.persona.name} on mykink.ai`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => <p>Persona does not exist</p>,
			}}
		/>
	)
}
// creative work json schema? https://schema.org/CreativeWork
